import {PRIORITY_POINTS} from '../constants'

/**
 * Độ ưu tiên và phát hiện cấu hình mơ hồ của nhóm điều kiện (FR-M16-06 · V-07 · §5.2).
 *
 * ⚠️ Điểm dễ làm sai nhất của cả chức năng: **cùng độ ưu tiên KHÔNG có nghĩa là xung đột**
 *    (BR-M16-43). "Gỗ 5 ly" và "Mica 2 ly" đều 10 điểm nhưng không bao giờ cùng khớp một dòng
 *    NVL nên hoàn toàn hợp lệ. Phải kiểm **giao nhau**, không chỉ so điểm. Nếu chỉ so điểm thì
 *    dòng 1 và dòng 4 của bảng §5.2 bị báo lỗi sai, chặn đúng cấu hình mà nghiệp vụ cần khai.
 *    SRS ghi rõ lỗi này đã xảy ra trên bản mockup.
 *
 * Hàm ở đây chỉ để phản hồi tại chỗ trên form. Bản quyết định cuối cùng là
 * `src/domain/priority.ts` của service (BR-M16-46) — khi lưu thất bại vì AMBIGUOUS_PAIR thì
 * lấy `details` từ service, không tin bản client.
 */

/** Nhóm càng cụ thể càng nhiều điểm. Nhóm ALL luôn 0 điểm và luôn thua mọi nhóm khác. */
export const priorityOf = (group) => {
    let points = PRIORITY_POINTS.ALL

    if (group.skuScope === 'list') points += PRIORITY_POINTS.SKU_LIST
    // PHẲNG: khai 1 NVL hay 5 NVL đều +10 — khớp `computedPriorityOf` của service.
    if ((group.materialCodes || []).length) points += PRIORITY_POINTS.MATERIAL

    return points
}

/** Phạm vi SKU giao nhau: cả hai ALL, một bên ALL, hoặc hai danh sách có ít nhất một mã chung. */
const skuScopeIntersects = (left, right) => {
    if (left.skuScope === 'all' || right.skuScope === 'all') return true

    const rightSet = new Set(right.skuList || [])

    return (left.skuList || []).some((sku) => rightSet.has(sku))
}

/**
 * Phạm vi NVL giao nhau: một bên để trống (= mọi NVL), hoặc hai danh sách có ít nhất MỘT mã chung.
 *
 * ⚠️ Giao nhau, KHÔNG phải bằng nhau: chỉ cần một mã chung là một dòng NVL rơi vào cả hai nhóm, tức
 *    đã mơ hồ. So bằng nhau sẽ bỏ lọt `['0205','0101']` ↔ `['0101']`.
 */
const materialScopeIntersects = (left, right) => {
    const leftCodes = left.materialCodes || []
    const rightCodes = right.materialCodes || []

    if (!leftCodes.length || !rightCodes.length) return true

    const rightSet = new Set(rightCodes)

    return leftCodes.some((code) => rightSet.has(code))
}

const toTime = (value) => (value ? new Date(value).getTime() : null)

/** Khoảng hiệu lực chồng lấn. Cận trên loại trừ: `effectiveFrom <= t < effectiveTo` (RB-03). */
const effectiveRangeOverlaps = (left, right) => {
    const leftFrom = toTime(left.effectiveFrom)
    const rightFrom = toTime(right.effectiveFrom)
    const leftTo = toTime(left.effectiveTo)
    const rightTo = toTime(right.effectiveTo)

    if (leftFrom === null || rightFrom === null) return true
    if (leftTo !== null && rightFrom >= leftTo) return false
    if (rightTo !== null && leftFrom >= rightTo) return false

    return true
}

/**
 * Nhóm có ĐANG CHẠY tại `at` không — `effectiveFrom <= at < effectiveTo`, cận trên LOẠI TRỪ.
 *
 * ⚠️ Bản sao có chủ đích của `isEffectiveAt` ở `src/domain/standardLookup.ts` phía service. Màn cấu
 *    hình phải chấm được các nhóm ĐANG SỬA (chưa gửi đi), nên không thể hỏi server từng lần gõ; mà
 *    chấm sai thì "đã phủ / chưa phủ" trên màn hình lệch với coverage và M17. Ba lần sửa liên tiếp
 *    chết ở đúng chỗ này: server sửa đúng, FE vẫn tính trên TẤT CẢ nhóm nên ghi đè câu trả lời đúng.
 *
 *    **Sửa vế nào thì phải sửa vế kia.** Hai file dưới đây giữ CÙNG một bảng ca, chép sang nhau, và
 *    là thứ duy nhất bắt người sửa nhìn sang phía kia:
 *      · `src/views/ProductionPlanning/helpers/__tests__/effectiveAt.contract.test.js` (repo này);
 *      · `production-performance/test/unit/domain/effective-at.contract.test.ts` — khoá cả bản domain
 *        lẫn bộ lọc Mongo.
 *
 *    ⚠️ Hai đường dẫn trên phải luôn TRỎ ĐÚNG. Bản trước của docblock này trỏ vào `test/contract/…`,
 *       thư mục không tồn tại trong repo — nên cơ chế bắc-qua-hai-repo trở thành lời dặn suông.
 *
 *    ⚠️ Và biết giới hạn của nó: repo webapp KHÔNG có job CI chạy test, nên vế này chỉ đỏ khi có
 *       người chạy tay (`CI=true npx react-scripts test --testPathPattern effectiveAt.contract`).
 *       Vế service thì gác cứng ở `test:unit`. Đây là lựa chọn đã chốt, không phải thiếu sót — nhưng
 *       nghĩa là sửa luật ở ĐÂY mà quên phía kia sẽ KHÔNG có gì chặn lại. Chạy tay trước khi gửi.
 *
 * Nhóm chưa chọn ngày (vừa thêm trên UI) coi như ĐANG CHẠY: người dùng đang dựng nó, chặn nó khỏi
 * mọi phép đếm sẽ làm cảnh báo nhấp nháy giữa chừng thao tác.
 */
export const isEffectiveAt = (group, at = new Date()) => {
    const from = toTime(group.effectiveFrom)

    if (from === null) return true

    const time = at.getTime()

    if (time < from) return false

    const to = toTime(group.effectiveTo)

    return to === null || time < to
}

/**
 * Nhóm MẶC ĐỊNH: mọi SKU + mọi NVL.
 *
 * Nó luôn 0 điểm và luôn thua mọi nhóm có điều kiện (BR-M16-44), không đặt được độ ưu tiên (VAL-17)
 * và không đặt được tên riêng (VAL-19). Khớp 1-1 với `isUnconditional` của service
 * (`src/domain/priority.ts`) — đó là bản CHUẨN, bản này là bản dịch sang JS.
 *
 * ⚠️ Vị từ này từng bị chép tay ở sáu chỗ (GroupCard ×2, effectivePriority, V-08, warningLabel, hasAllGroup).
 *    Service đã gom về một hàm vì đúng lý do đó; FE gom theo. Thêm chỗ dùng thì gọi hàm này.
 */
export const isDefaultGroup = (group) => group.skuScope === 'all' && !(group.materialCodes || []).length

/**
 * Nhóm này đang dùng số ĐẶT TAY hay điểm chấm tự động?
 *
 * ⚠️ KHÔNG suy ra từ `typeof group.priority === 'number'`. Service trả về `priority` là điểm
 *    HIỆU LỰC (đã gộp cả hai nguồn) nên nhóm chấm tự động cũng có số — suy kiểu đó thì mỗi lần tải
 *    lên rồi lưu lại là đóng băng điểm tự động thành số đặt tay, và từ đó nhóm không còn đổi theo
 *    luật nữa. Cờ phân biệt là `prioritySource` do service gửi kèm.
 *
 * Nhóm mới thêm tại chỗ chưa có cờ nào: coi là tự động cho tới khi người dùng sửa ô.
 */
export const isManualPriority = (group) => {
    if (group.prioritySource) return group.prioritySource === 'manual'

    return typeof group.priority === 'number'
}

/**
 * Độ ưu tiên THẬT SỰ dùng để so sánh: lấy số người dùng đặt tay nếu có, không thì chấm theo điều kiện.
 *
 * Nhóm ALL (tất cả SKU + không khai NVL) luôn ép về 0 và không nhận số đặt tay — BR-M16-44 nói nó
 * luôn thua mọi nhóm khác. Cho nhóm ALL trèo lên trên thì mọi nhóm có điều kiện thành vô nghĩa.
 * Service cưỡng chế đúng luật này và trả 400 VAL-17 nếu payload gửi số khác 0 cho nhóm ALL.
 */
export const effectivePriority = (group) => {
    if (isDefaultGroup(group)) return PRIORITY_POINTS.ALL
    if (isManualPriority(group)) return group.priority

    return priorityOf(group)
}

/** Tóm tắt điều kiện của nhóm, dùng cho nhãn G.2 và cho nội dung thông báo V1.3. */
export const groupLabel = (group, materialName) => {
    // "Mọi SKU" chứ không phải "Tất cả SKU": khớp ĐÚNG chuỗi mà `describeScope` của service in ra
    // trong cảnh báo VAL-08 (`warnings[].scope`) và lỗi VAL-09. Người dùng đọc "Mọi SKU · NVL 0205"
    // rồi quét màn hình tìm thẻ mang tên đó — hai cách gọi là họ không nối được hai dòng với nhau.
    const scope = group.skuScope === 'all' ? 'Mọi SKU' : `${(group.skuList || []).length} SKU`
    const codes = group.materialCodes || []
    // Một tên khi đúng một mã (gọi tên dễ đọc hơn mã); nhiều mã thì liệt kê mã cho ngắn.
    const material = codes.length === 1
        ? ` · ${materialName || codes[0]}`
        : (codes.length ? ` · ${codes.length} NVL` : '')

    return `${scope}${material}`
}

/**
 * Nhãn cho nhóm ĐÃ KHỚP ở màn tra cứu.
 *
 * `GET /config/standard-time/lookup` chỉ trả `{status, groupId, rate, priority}` — không có số
 * thứ tự lẫn tên nhóm, và `groupId` là ObjectId đọc lên không ai hiểu. Thứ duy nhất mô tả được nhóm
 * là điểm độ ưu tiên; may là điểm đó cộng từ đúng hai điều kiện nên suy ngược ra được nhóm đã khớp
 * theo danh sách SKU, theo NVL, hay là nhóm ALL.
 *
 * Khác `groupLabel` ở trên: hàm kia cần cả object nhóm (có sẵn khi đang sửa cấu hình), hàm này chỉ
 * có mỗi con điểm.
 */
export const matchedGroupLabel = (priority, lookup) => {
    // ⚠️ ĐỪNG đoán ngược từ điểm khi service đã nói thẳng. Từ khi độ ưu tiên đặt tay được, một nhóm
    //    bất kỳ có thể mang đúng 0/10/50/60 — bốn giá trị mà cách chấm tự động sinh ra — nên suy
    //    ngược sẽ gọi SAI tên nhóm và người dùng đi sửa nhầm nhóm.
    //    `groupScope` + `prioritySource` do service trả kèm (`lookupResultDto`) là nguồn đúng.
    const scope = lookup && lookup.groupScope

    if (scope) {
        const sku = scope.skuScope === 'all' ? 'Mọi SKU' : `${scope.skuCount} SKU cụ thể`
        const material = (scope.materialCodes || []).length ? ` · NVL ${scope.materialCodes.join(', ')}` : ' · mọi NVL'
        const manual = lookup.prioritySource === 'manual' ? ' (độ ưu tiên đặt tay)' : ''

        return `${sku}${material}${manual}`
    }

    const score = Number(priority) || 0

    // ⚠️ Suy ngược CHỈ đúng với điểm chấm tự động. Từ khi độ ưu tiên đặt tay được, một nhóm có thể
    //    mang điểm bất kỳ (0…1000) — điểm 7 suy ngược ra "Nhóm ALL" là NÓI SAI, và ở màn tra cứu thì
    //    nói sai còn tệ hơn không nói: người dùng đi sửa nhầm nhóm. Chỉ có bốn tổ hợp là hợp lệ với
    //    cách chấm tự động, ngoài bốn số đó thì thừa nhận là không suy được.
    const AUTO_SCORES = {
        0: 'Nhóm mặc định — mọi SKU, mọi NVL',
        [PRIORITY_POINTS.MATERIAL]: 'Nhóm theo nguyên vật liệu',
        [PRIORITY_POINTS.SKU_LIST]: 'Nhóm theo danh sách SKU',
        [PRIORITY_POINTS.SKU_LIST + PRIORITY_POINTS.MATERIAL]: 'Nhóm theo danh sách SKU + NVL',
    }

    return AUTO_SCORES[score] || `Nhóm đặt độ ưu tiên tay (${score} điểm)`
}

/**
 * Trả về các cặp nhóm bị coi là cấu hình mơ hồ. Một cặp xung đột khi thoả **tất cả** bốn điều
 * kiện, không phải chỉ điều kiện độ ưu tiên.
 */
export const findConflicts = (groups) => {
    const conflicts = []

    for (let i = 0; i < groups.length; i += 1) {
        for (let j = i + 1; j < groups.length; j += 1) {
            const left = groups[i]
            const right = groups[j]

            // So theo điểm THẬT: đặt tay hai số khác nhau là đã tự phân định thắng thua, không
            // còn mơ hồ nữa. So theo điểm chấm tự động thì vẫn báo lỗi oan.
            if (effectivePriority(left) !== effectivePriority(right)) continue
            if (!effectiveRangeOverlaps(left, right)) continue
            if (!skuScopeIntersects(left, right)) continue
            if (!materialScopeIntersects(left, right)) continue

            conflicts.push({
                left: {index: i, priority: effectivePriority(left)},
                right: {index: j, priority: effectivePriority(right)},
            })
        }
    }

    return conflicts
}

/**
 * Toàn bộ kiểm tra của form SCR-M16-002 (§5.1 · Phụ lục A.2). Trả về hai danh sách tách rời:
 * `errors` chặn lưu, `warnings` không chặn. Trộn hai mức làm người dùng thấy màn hình đỏ liên
 * tục rồi bỏ qua toàn bộ cảnh báo (§0.3).
 *
 */
/**
 * Nhóm có KHAI chỉ số 0 hay không — khác hẳn "ô trống".
 *
 * ⚠️ Lọc bằng `Number(rate) === 0` thôi là chưa đủ: ô TRỐNG cũng cho `Number(null) === 0`. Đây là
 *    đường phân thuỷ giữa "chưa nhập" (V-04, lỗi chặn lưu) và "cố ý khai 0" (V-12, chỉ cảnh báo),
 *    nên nó phải nằm ở MỘT chỗ. Chép tay vị từ này ra nhiều nơi thì sớm muộn hai chỗ lệch nhau, và
 *    lệch ở đây nghĩa là thẻ hiện tag "0 giây — không qua công đoạn" trong khi `validateGroups`
 *    không sinh V-12, hoặc ngược lại.
 */
export const isZeroRate = (group) => (
    group.rate !== null && group.rate !== undefined && group.rate !== '' && Number(group.rate) === 0
)

/**
 * Nhóm đã HẾT hiệu lực — mốc kết thúc đã qua. Khác hẳn "chưa tới hiệu lực".
 *
 * ⚠️ Đừng dùng `!isEffectiveAt(...)` để thay cho hàm này. `isEffectiveAt` trả `false` cho CẢ hai
 *    ca: đã hết hạn VÀ chưa tới hạn. Gộp chúng lại là chỗ vòng review 12 bắt được một hồi quy:
 *    nhóm khai trước cho kỳ sau bị tắt cảnh báo ở đúng lúc người dùng còn đứng trước màn hình.
 */
export const isExpiredAt = (group, at = new Date()) => {
    const to = toTime(group.effectiveTo)

    return to !== null && at.getTime() >= to
}

/**
 * Nhóm khai chỉ số 0 và cảnh báo còn Ý NGHĨA — vị từ DÙNG CHUNG giữa thẻ nhóm và `validateGroups`.
 *
 * ⚠️ Cả hai phía PHẢI gọi hàm này, đừng bên gọi `isZeroRate` trần bên gọi kèm bộ lọc hiệu lực.
 *    Lệch một phía nghĩa là thẻ hiện tag cam "0 giây — không qua công đoạn" trong khi dải cảnh báo
 *    im, hoặc ngược lại — người dùng không nối được hai thứ với nhau. Vòng 12 và 14 đều phạt đúng
 *    chỗ này.
 *
 * Nhóm CHƯA tới hiệu lực vẫn cảnh báo: khoảnh khắc bấm Lưu là lúc DUY NHẤT người dùng còn ở đây,
 * và hậu quả (tắt cảnh báo "thiếu định mức" của cả công đoạn) sẽ xảy ra thật khi tới ngày.
 * Chỉ nhóm đã ĐÓNG mới im, vì nó không còn tác động gì nữa.
 */
export const isActiveZeroRate = (group, at = new Date()) => isZeroRate(group) && !isExpiredAt(group, at)

/** Hậu tố đặt cho bản sao của một nhóm. */
export const COPY_SUFFIX = ' (bản sao)'

/**
 * Tên cho bản sao của một nhóm, cắt vừa trần `maxLength`.
 *
 * ⚠️ Cắt phần GỐC trước rồi mới nối hậu tố. Nối trước rồi `slice(0, maxLength)` thì tên gốc dài đúng
 *    trần sẽ bị cắt sạch hậu tố và bản sao mang tên Y HỆT bản gốc — hai thẻ trùng tên nằm sát nhau,
 *    đúng thứ việc đặt tên bản sao sinh ra để tránh. Tên 72-79 ký tự thì ra hậu tố cụt kiểu "(bản s".
 */
export const copyGroupName = (name, maxLength) => {
    if (!name) return name

    return `${`${name}`.slice(0, maxLength - COPY_SUFFIX.length)}${COPY_SUFFIX}`
}

export const validateGroups = (
    groups,
    {materialCodes = [], byDetect = true} = {}
) => {
    const errors = []
    // `warnings` KHÔNG chặn lưu — xem docblock. Trộn hai mức làm người dùng thấy màn hình đỏ liên
    // tục rồi bỏ qua toàn bộ cảnh báo.
    const warnings = []
    const knownMaterials = new Set(materialCodes)

    groups.forEach((group, index) => {
        const number = index + 1

        // V-01 — chọn "Danh sách cụ thể" thì phải có ít nhất một SKU.
        if (group.skuScope === 'list' && !(group.skuList || []).length) {
            errors.push({
                code: 'V-01',
                message: `Nhóm ${number}: chọn ‘Danh sách cụ thể’ nhưng chưa nhập SKU nào`,
            })
        }

        // V-02 — NVL phải có trong danh mục. V-03: công đoạn by_item thì ô bị khoá, không kiểm.
        const groupMaterials = group.materialCodes || []
        const unknownMaterials = knownMaterials.size
            ? groupMaterials.filter((code) => !knownMaterials.has(code))
            : []

        // V-02 — KHÔNG chặn lưu.
        //
        // ⚠️ Đường PUT của service không đối chiếu `materialCodes` với danh mục (chỉ đường import
        //    có). Chặn ở client trong khi server cho qua nghĩa là: một nhóm cũ chứa mã NVL sau đó bị
        //    gỡ khỏi danh mục FAC sẽ khoá MỌI thao tác lưu trên công đoạn đó — kể cả sửa một chỉ số
        //    không liên quan — mà không có đường nào thoát ngoài tự tay gỡ mã. Báo để người dùng
        //    biết mà dọn, không khoá họ lại.
        if (byDetect && unknownMaterials.length) {
            warnings.push({
                code: 'V-02',
                message: `Nhóm ${number}: NVL ‘${unknownMaterials.join(', ')}’ không còn trong danh mục — nên gỡ khỏi nhóm`,
            })
        }

        // V-04 — chỉ số bắt buộc, và không được âm.
        //
        // `0` là giá trị HỢP LỆ: nó nói "SKU/NVL này không đi qua công đoạn", tức 0 giờ THẬT. Khác
        // hẳn ô để TRỐNG, nghĩa là chưa ai khai.
        //
        // ⚠️ Phải kiểm RỖNG riêng, trước khi ép số. `Number(null)` và `Number('')` đều ra `0`, nên
        //    một điều kiện gọn kiểu `!(Number(rate) >= 0)` sẽ cho ô trống lọt qua thành "hợp lệ, 0
        //    giây" — âm thầm biến mọi nhóm quên điền thành "không qua công đoạn", đúng thứ nguy hiểm
        //    nhất mà việc mở khoá số 0 mang lại. antd `InputNumber` trả `null` khi người dùng xoá ô.
        const rawRate = group.rate

        if (rawRate === null || rawRate === undefined || rawRate === '') {
            errors.push({code: 'V-04', message: `Nhóm ${number}: chưa nhập chỉ số`})
        } else if (!Number.isFinite(Number(rawRate)) || Number(rawRate) < 0) {
            errors.push({
                code: 'V-04',
                message: `Nhóm ${number}: chỉ số không được âm (dùng 0 để khai “không đi qua công đoạn này”)`,
            })
        }

        // V-06 — chỉ bắt buộc CÓ ngày hiệu lực.
        //
        // ⚠️ KHÔNG chặn ngày quá khứ nữa. Nhập định mức lùi ngày là việc có thật (IE đo xong mới nhập
        //    cho kỳ trước) và service chấp nhận — chặn ở client trong khi server cho qua là hai tầng
        //    nói hai kiểu, và người dùng không có đường nào làm việc hợp lệ đó trên màn hình. Service
        //    trả cảnh báo VAL-08 kèm response, màn hình hiện lại sau khi lưu.
        const from = group.effectiveFrom ? new Date(group.effectiveFrom) : null

        if (!from) {
            errors.push({code: 'V-06', message: `Nhóm ${number}: chưa chọn ngày hiệu lực`})
        }

        // V-09 — mốc kết thúc phải SAU mốc bắt đầu. Cận trên LOẠI TRỪ nên bằng nhau là khoảng RỖNG:
        // nhóm không bao giờ khớp gì. Service chặn y hệt; ở đây chỉ để báo sớm.
        const to = group.effectiveTo ? new Date(group.effectiveTo) : null

        if (from && to && to.getTime() <= from.getTime()) {
            errors.push({
                code: 'V-09',
                message: `Nhóm ${number}: ngày kết thúc phải SAU ngày hiệu lực`,
            })
        }
    })

    // V-07 — cấu hình mơ hồ.
    findConflicts(groups).forEach(({left, right}) => {
        errors.push({
            code: 'V-07',
            message: `Cấu hình mơ hồ: Nhóm ${left.index + 1} ↔ Nhóm ${right.index + 1} cùng độ ưu tiên và có thể cùng khớp một dòng NVL`,
        })
    })

    // V-08 — tối đa một nhóm ALL **đang hiệu lực cùng lúc** trên mỗi công đoạn.
    //
    // ⚠️ Phải xét khoảng hiệu lực, không chỉ đếm. Đóng nhóm ALL cũ (đặt "Đến ngày") rồi khai nhóm
    //    ALL mới cho kỳ sau là ĐÚNG quy trình mà ô "Đến ngày" sinh ra để làm — service chấp nhận
    //    (hai khoảng rời nhau thì `effectiveRangesOverlap` trả false). Đếm trần như trước là client
    //    chặn đúng thứ server cho phép, và người dùng không có đường nào lách.
    const allGroups = groups.filter(isDefaultGroup)
    const overlappingAll = allGroups.some((left, i) =>
        allGroups.slice(i + 1).some((right) => effectiveRangeOverlaps(left, right))
    )

    if (overlappingAll) {
        errors.push({code: 'V-08', message: 'Hai nhóm mặc định có khoảng hiệu lực chồng lấn — chỉ được một nhóm mặc định tại một thời điểm'})
    }

    // V-12 — nhóm khai chỉ số 0. CẢNH BÁO, KHÔNG chặn lưu: đây là cấu hình hợp lệ và cố ý.
    //
    // ⚠️ Lọc trên `Number(rate) === 0` thôi là chưa đủ — ô TRỐNG cũng cho `Number(null) === 0`. Dòng
    //    trống đã có V-04 lo; gom cả nó vào đây thì người dùng nhận hai thông báo mâu thuẫn cho cùng
    //    một ô ("chưa nhập" và "đang khai 0 giây").
    /*
     * Loại nhóm đã ĐÓNG, giữ nhóm chưa tới hiệu lực. Xem docblock `isActiveZeroRate`: gộp hai ca
     * đó lại là hồi quy vòng 12 — nhóm khai trước cho kỳ sau bị tắt cảnh báo ở đúng lúc người dùng
     * còn đứng trước màn hình, rồi tới ngày hiệu lực thì hậu quả xảy ra mà không ai từng được báo.
     */
    const zeroGroups = groups.filter((group) => isActiveZeroRate(group))

    if (zeroGroups.length) {
        warnings.push({
            code: 'V-12',
            message:
                `${zeroGroups.length} nhóm đang khai chỉ số 0 — nghĩa là “không đi qua công đoạn này” (0 giờ thật, ` +
                'và vẫn tính là ĐÃ có định mức). Nếu thực ra là chưa đo được thì hãy XOÁ nhóm, đừng để 0.',
        })
    }

    // Ca nguy hiểm nhất, tách riêng vì hệ quả khác hẳn: nhóm mặc định phủ MỌI SKU chưa có nhóm riêng,
    // nên để 0 ở đó là tắt hẳn cảnh báo thiếu định mức của cả công đoạn — màn hình im lặng hoàn toàn
    // thay vì báo "còn N SKU thiếu". PO đã chốt cho phép, nên chỉ cảnh báo.
    if (zeroGroups.some(isDefaultGroup)) {
        warnings.push({
            code: 'V-12',
            message:
                'Nhóm mặc định đang khai chỉ số 0 — MỌI SKU chưa có nhóm riêng sẽ được coi là không đi qua công ' +
                'đoạn này, và cảnh báo “thiếu định mức” của công đoạn sẽ tắt hoàn toàn.',
        })
    }

    return {errors, warnings}
}
